import React from 'react';
import { cellDisplayText, cellTooltipText, formatCellValue } from './formatCellValue';

export type TruncatedCellProps = {
  value?: unknown;
  children?: React.ReactNode;
  maxWidth?: number | string;
  className?: string;
  tooltip?: boolean;
  /** Full tooltip text when display value is truncated or formatted. */
  title?: string;
  copyable?: boolean;
  sensitive?: boolean;
  href?: string;
};

export default function TruncatedCell({
  value,
  children,
  maxWidth,
  className = '',
  tooltip = true,
  title: titleOverride,
  copyable = false,
  sensitive = false,
  href,
}: TruncatedCellProps) {
  const formatted = value !== undefined ? formatCellValue(value) : '';
  const text = children ?? cellDisplayText(formatted, sensitive);
  const title = tooltip
    ? titleOverride ?? (formatted ? cellTooltipText(formatted, sensitive) : undefined)
    : undefined;
  const style = maxWidth !== undefined ? { maxWidth } : undefined;
  const classes = ['truncated-cell', className].filter(Boolean).join(' ');

  async function copyValue() {
    if (sensitive || formatted === '—') return;
    try {
      await navigator.clipboard.writeText(formatted);
    } catch {
      /* clipboard may be unavailable */
    }
  }

  const content =
    href && !sensitive && formatted !== '—' ? (
      <a
        href={href}
        className={classes}
        style={style}
        title={title}
        aria-label={title}
        target="_blank"
        rel="noopener noreferrer"
      >
        {text}
      </a>
    ) : (
      <span className={classes} style={style} title={title} aria-label={title}>
        {text}
      </span>
    );

  if (!copyable || sensitive || (formatted === '—' && !children)) {
    return content;
  }

  return (
    <span className="truncated-cell-wrap">
      {content}
      <button
        type="button"
        className="truncated-cell-copy"
        aria-label={`Copy ${formatted}`}
        title="Copy"
        onClick={() => void copyValue()}
      >
        Copy
      </button>
    </span>
  );
}
